import { useEffect, useState } from 'react';

/**
 * Client for /api/user/access (backend/chainlit/access_api.py).
 *
 * One shared store: the header pill, the end screen in App and the welcome
 * brief all read the same answer, so they can never disagree about whether
 * access has ended.
 *
 * The wording built on it never says "demo" or "trial". The same date is a
 * paying customer's renewal date.
 */

export interface Access {
  status: 'unlimited' | 'active' | 'ending' | 'ended';
  show: boolean;
  soon: boolean;
  days_left: number | null;
  /** The last day with access, YYYY-MM-DD. */
  last_day: string | null;
  renew_email: string;
  renew_cc?: string[];
  /** When this user last asked to renew for the current period, if they did. */
  renewal_requested_at?: string | null;
}

/** Opens the one renewal dialog (RenewDialog), from any button. */
export const OPEN_RENEW_EVENT = 'signals-open-renew';
export const openRenewDialog = () =>
  window.dispatchEvent(new CustomEvent(OPEN_RENEW_EVENT));

export type RenewResult =
  | { ok: true; sentAt: string }
  | { ok: false; message: string };

/** Sends the request; on success the store re-reads so every button flips to
 *  "Request sent" together. */
export async function requestRenewal(note: string): Promise<RenewResult> {
  try {
    const r = await fetch('/api/user/access/renew', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note.trim() || null })
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok)
      return {
        ok: false,
        message:
          d?.detail ||
          "We couldn't send your request. Please try again in a few minutes."
      };
    check();
    return { ok: true, sentAt: d.sent_at };
  } catch {
    return {
      ok: false,
      message:
        "We couldn't send your request. Check your connection and try again."
    };
  }
}

/** Midnight in Cairo moves the count, and an admin may extend an account at
 *  any time: asking every 10 minutes and on tab focus covers both cheaply
 *  (one primary-key read). */
const REFRESH_MS = 10 * 60 * 1000;

let state: Access | null = null;
/** True once the first answer (or failure) is in, so App knows it may connect. */
let checked = false;
let started = false;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

function check() {
  fetch('/api/user/access', { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (d) state = d;
    })
    .catch(() => {
      /* keep what we last knew: a network blip must not lock anyone out */
    })
    .finally(() => {
      checked = true;
      emit();
    });
}

function start() {
  if (started) return;
  started = true;
  check();
  window.setInterval(check, REFRESH_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
}

export function useAccess(enabled = true) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const l = () => setTick((t) => t + 1);
    listeners.add(l);
    start();
    return () => {
      listeners.delete(l);
    };
  }, [enabled]);
  return { access: state, checked };
}

/** "5 Nov" from YYYY-MM-DD, without a timezone shift. */
export function dayMonth(iso: string | null): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC'
  });
}
